import {FC, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  checkCompliance,
  selectEditOrder,
  selectEditOrderId,
  selectSubtotalPrice,
  setOrderEditDiscounts,
} from 'app/features/orders/redux/order/orderEditSlice'
import {RootState} from 'setup'
import PromoCodeSelection from './PromoCodeSelection'
import DiscountInput from './DiscountInput'
import orderEditService from 'app/services/OrderEditService/OrderEditService'
import {ShippingMethodDialog} from '../deliveryMethodAction'
import {Button, ErrorMessage} from '_core'
import {Order} from 'app/features/orders/models/EditOrderState'
import {deliveryTypes} from 'app/constant'
import {OrderModal, OrderModalButton} from 'app/features/orders/componentsv1'
import {simpleCurrency} from 'app/format/currency'
import {discountValueType} from 'app/features/orders/models/DiscountCode'

type Props = {
  onClose: () => void
  isWineClub?: boolean
  title?: string
  subTitle?: string
  discountCodeId?: string
}

const EditDiscount: FC<Props> = ({
  title = 'Add Discount',
  subTitle = 'Select Promo Code or Enter in a Fixed Amount',
  onClose,
  isWineClub = false,
  discountCodeId,
}) => {
  const dispatch = useDispatch()
  const [discount, setDiscount] = useState<Discount>(initialState)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const subTotal = useSelector<RootState, any>(selectSubtotalPrice)
  const [discountTypeSelected, setDiscountTypeSelected] = useState(discountType.promoCode)
  const editOrderId = useSelector<RootState, string>(selectEditOrderId)
  const [showDialog, setShowDialog] = useState(false)
  const order = useSelector<RootState, Order>(selectEditOrder)
  const [isValidating, setIsValidating] = useState(false)
  const [error, setError] = useState('')

  const discountSubtotal: number | any = useMemo(() => {
    let total = subTotal
    if (order.wineClubDiscounts > 0 && !isWineClub) {
      total -= order.wineClubDiscounts
    }
    if (order.totalDiscounts > 0 && isWineClub) {
      total -= order.totalDiscounts
    }
    return parseFloat(total)
  }, [subTotal, order.wineClubDiscounts, order.totalDiscounts, isWineClub])

  const discountAmount = useMemo(() => {
    if (discount) {
      return calculateDiscount({...discount, subTotal: discountSubtotal})
    }
    return 0
  }, [discount, discountSubtotal])

  const showWarning = useMemo(() => {
    if (discountTypeSelected === discountType.fixedAmount) {
      return parseFloat(discountSubtotal) < parseFloat(discount.discountValue)
    }
    return false
  }, [discount.discountValue, discountSubtotal, discountTypeSelected])

  const saveDiscount = async () => {
    try {
      setIsSubmitting(true)
      const model: any = {
        discountAmount,
        pDiscountCodeId: discount?.id,
        pOrderEditId: editOrderId,
        pIsWineClub: isWineClub,
        pDiscountId: discountCodeId,
      }

      if (discountTypeSelected === discountType.percent) {
        model.pPercentage = discount.discountValue
      }
      const discounts = await orderEditService.saveOrderEditDiscount(model)
      dispatch(setOrderEditDiscounts(discounts))
      if (
        (discount.discountType === 'SHIPPING' || discount.discountType === 'FREE_SHIPPING') &&
        order.orderDeliveryMethod === deliveryTypes.ship
      ) {
        setShowDialog(true)
        setIsSubmitting(false)
        return
      } else {
        return true
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handelDiscountCode = async () => {
    try {
      if (await saveDiscount()) {
        dispatch(checkCompliance())
        onClose()
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSelectDiscountType = (e: any) => {
    const {value}: any = e.target
    setDiscountTypeSelected(value)
    setDiscount(initialState)
    setError('')
  }

  const validate = (input: any) => {
    const {value} = input
    if (value <= 100) {
      return true
    }
    return false
  }

  const handleChangePercentage = (discountValue: number | any) => {
    setDiscount({valueType: discountValueType.PERCENTAGE, discountValue})
  }

  const handleChangeFixedAmount = (discountValue: number | any) => {
    setDiscount({valueType: discountValueType.FIXED_AMOUNT, discountValue})
  }

  const handleClose = () => {
    setShowDialog(false)
    onClose()
  }

  if (showDialog) {
    return <ShippingMethodDialog onClose={handleClose} />
  }

  return (
    <OrderModal
      title={title}
      onClose={onClose}
      hideFooter
      icon='percentage-discount'
      subTitle={subTitle}
    >
      {!isWineClub && (
        <DiscountInput
          label='Promo Code'
          value={discountType.promoCode}
          onChange={handleSelectDiscountType}
          checked={discountType.promoCode === discountTypeSelected}
          disabled={isValidating}
        >
          <PromoCodeSelection
            onDiscount={setDiscount}
            discount={discount}
            isPromoCode={discountType.promoCode === discountTypeSelected}
            onIsValidating={setIsValidating}
            onError={setError}
          />
        </DiscountInput>
      )}
      {isValidating && (
        <div className='w-100 d-flex justify-content-center align-items-center position-absolute top-50'>
          <label>
            <span className='indicator-progress d-block'>
              <span className='spinner-border align-middle ms-5 text-info'></span>
            </span>
            Loading...
          </label>
        </div>
      )}
      <div className='d-flex gap-2 mt-6 w-150'>
        <DiscountInput
          label='Fixed Amount ($)'
          value={discountType.fixedAmount}
          onChange={handleSelectDiscountType}
          input={{
            value: discountType.fixedAmount === discountTypeSelected && discount.discountValue,
            onChange: handleChangeFixedAmount,
            name: 'fixedAmount',
            disabled: discountType.fixedAmount !== discountTypeSelected,
            showWarning: discountType.fixedAmount === discountTypeSelected && showWarning,
          }}
          checked={discountType.fixedAmount === discountTypeSelected}
          disabled={isValidating}
        />
        <DiscountInput
          label='Percentage (%)'
          value={discountType.percent}
          onChange={handleSelectDiscountType}
          input={{
            value: discountType.percent === discountTypeSelected && discount.discountValue,
            onChange: handleChangePercentage,
            name: 'percent',
            isAllowed: validate,
            disabled: discountType.percent !== discountTypeSelected,
          }}
          checked={discountType.percent === discountTypeSelected}
          disabled={isValidating}
        />
      </div>

      {showWarning && discountSubtotal > 0 && (
        <div className='pt-4'>
          <ErrorMessage
            error={`discount cannot be greater than ${simpleCurrency(discountSubtotal)}`}
          />
        </div>
      )}

      <ErrorMessage error={error} />

      <div className='pt-5'>
        <OrderModalButton
          type='submit'
          isSubmitting={isSubmitting}
          onClose={onClose}
          disabled={!discount?.discountValue}
        >
          <Button
            id='btnConfirm'
            title='Confirm'
            className='btn btn-primary-active w-100'
            onClick={handelDiscountCode}
            isSubmitting={isSubmitting}
            disabled={!discount?.discountValue || showWarning || isValidating}
          />
        </OrderModalButton>
      </div>
    </OrderModal>
  )
}
export default EditDiscount

const calculateDiscount = ({valueType, discountValue, maximumAmount, subTotal}: any) => {
  subTotal = parseFloat(subTotal)
  discountValue = parseFloat(discountValue)
  if (valueType === discountValueType.PERCENTAGE) {
    const discountAmount = (discountValue / 100) * subTotal
    if (maximumAmount > 0 && discountAmount > maximumAmount) {
      return parseFloat(maximumAmount)
    }
    return discountAmount
  }

  return subTotal <= discountValue ? subTotal : discountValue
}

const discountType = {
  promoCode: 'promoCode',
  fixedAmount: 'fixedAmount',
  percent: 'percent',
}

const initialState: Discount = {
  id: null,
  valueType: null,
  discountValue: null,
  maximumAmount: null,
}

type Discount = {
  id?: string | null
  valueType: string | any
  discountValue: number | any
  maximumAmount?: number | any
  discountType?: string
}
